import { email, firstName, lastName } from '@/schemas';
import { ChangeEvent, useState } from 'react';
import * as Yup from 'yup';
import { FormFieldElement } from '../FormField/interfaces';
import { CourseMemberFieldNames, CourseMembers } from './types';

const memberFormSchema = Yup.array()
  .of(
    Yup.object({
      firstName,
      lastName,
      email,
    })
  )
  .min(1);

const createMemberSchemaPath = (
  memberIndex: number,
  fieldName: CourseMemberFieldNames
) => `[${memberIndex}].${fieldName}`;

const createMemberById = (id: number) => ({
  id,
  firstName: '',
  lastName: '',
  email: '',
});

export const useCourseMembersForm = () => {
  const [members, setMembers] = useState<CourseMembers>(() => [
    createMemberById(1),
  ]);

  const [errors, setErrors] = useState<{
    [schemaPath: string]: string;
  }>({});

  const handleBlur =
    (memberIndex: number, fieldName: CourseMemberFieldNames) => async () => {
      const schemaPath = createMemberSchemaPath(memberIndex, fieldName);

      try {
        await memberFormSchema.validateAt(schemaPath, members);
      } catch (error) {
        const currentError =
          error instanceof Yup.ValidationError
            ? error.message
            : 'Validierungsproblem';

        setErrors((errors) => ({
          ...errors,
          [schemaPath]: currentError,
        }));
      }
    };

  const handleChange = (
    memberIndex: number,
    fieldName: CourseMemberFieldNames
  ) => {
    const schemaPath = createMemberSchemaPath(memberIndex, fieldName);

    return ($event: ChangeEvent<FormFieldElement>) => {
      setMembers((members) => {
        const newMembers = structuredClone(members);

        newMembers[memberIndex] = {
          ...newMembers[memberIndex],
          [fieldName]: $event?.target.value,
        };

        return newMembers;
      });

      setErrors((errors) => {
        return {
          ...errors,
          [schemaPath]: '',
        };
      });
    };
  };

  const createNewMember = () => {
    setMembers((members) => [
      ...members,
      createMemberById((members.at(-1)?.id ?? 0) + 1),
    ]);
  };

  const removeMember = (memberId: number) => () => {
    setMembers((members) => members.filter(({ id }) => memberId !== id));

    setErrors((errors) => {
      return Object.keys(errors).reduce<typeof errors>((errors, key) => {
        if (key.startsWith(`[${memberId}]`)) {
          return errors;
        }

        return {
          ...errors,
          [key]: errors[key],
        };
      }, {});
    });
  };

  const getFieldProps = (
    memberIndex: number,
    fieldName: CourseMemberFieldNames
  ) => {
    return {
      value: members[memberIndex]?.[fieldName] ?? '',
      onChange: handleChange(memberIndex, fieldName),
      onBlur: handleBlur(memberIndex, fieldName),
      errorMessage:
        errors?.[createMemberSchemaPath(memberIndex, fieldName)] ?? '',
    };
  };

  const revalidateForm = async () => {
    let newErrors = {};

    try {
      await memberFormSchema.validate(members, {
        abortEarly: false,
        recursive: true,
      });
    } catch (error) {
      if (!(error instanceof Yup.ValidationError)) {
        return;
      }

      newErrors = error.inner.reduce<typeof errors>(
        (errors, { message, name, path }) => {
          return {
            ...errors,
            [`${path ?? name}`]: message,
          };
        },
        {}
      );
    }

    setErrors(() => newErrors);
  };

  return {
    isMembersFormValid: Object.values(errors).filter(Boolean).length === 0,
    members,
    createNewMember,
    removeMember,
    getFieldProps,
    revalidateForm,
  };
};
